import {
    ErrorOutlineRounded as FailedIcon,
    CheckCircleOutlineRounded as SentIcon,
} from '@mui/icons-material';
import { namespace } from '@common-config/constants';
import { KIND_GLYPH } from '@common/components/kind-glyphs/kind-glyphs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './calendar-grid.module.scss';
import { buildChipViewModel } from './layout/chip-view-model';
import { planWithoutImage, type ChipContentPlan } from './layout/chip-content';
import { planLeadMarks, type LeadGlyph, type LeadTone } from './layout/lead-glyph';
import { chipThumbnailSrc } from './layout/chip-thumbnail';
import { canDragEvent } from './layout/drag-model';
import { planOccurrenceDelete } from './layout/delete-occurrence';
import { useAssetUrl } from '@common/services/asset-url-resolver';
import { usePointedDelete } from './use-pointed-delete';
import type { CalendarLeanEvent } from './types';

export type ChipSize = 'month' | 'time' | 'panel';

/** Nothing is competing for width, so nothing is dropped. */
const FULL_CHIP = { image: true, text: true, time: true } as const;

interface Props {
    event: CalendarLeanEvent;
    now: number;
    size?: ChipSize;
    isDragging?: boolean;
    /** What this chip has room to render, decided by `planChipContent` from the
     *  lane width. Omitted where width is not a constraint (month, day panel),
     *  and on the first paint before the grid has been measured — a full chip is
     *  the safe default, since dropping parts is the exception. */
    content?: ChipContentPlan;
    onOpen: (event: CalendarLeanEvent, mouse: { x: number; y: number }) => void;
    onDragStart: (event: CalendarLeanEvent, e: React.PointerEvent<HTMLElement>) => void;
    /** Hover preview. The card itself is owned by the grid (one card, not one
     *  per chip), so the chip only reports where it is. */
    onHoverStart?: (event: CalendarLeanEvent, rect: DOMRect) => void;
    onHoverEnd?: () => void;
    /** Remove the ONE occurrence this chip stands for. Absent on surfaces that
     *  do not offer it; a chip that is handed no handler shows no affordance and
     *  swallows no key. `mouse` is where the gesture happened — #592c uses it to
     *  anchor the recurring-drip skip menu at the pointer. */
    onDelete?: (event: CalendarLeanEvent, mouse?: { x: number; y: number }) => void;
}

// A campaign reads as a campaign at a glance: the megaphone the rest of the app
// uses for one, a poll glyph for a poll, a bubble for a plain message, the
// segmented ring for a WhatsApp Status. OUTLINED weights: the filled versions
// are heavy enough at 16px to read as blobs and to out-shout the message text
// they sit next to. The kind glyphs come from the table the sidebar rows read
// too, so one kind looks the same on both surfaces.
const LEAD_ICON: Record<LeadGlyph, React.ComponentType<any>> = {
    ...KIND_GLYPH,
    failed: FailedIcon,
    sent: SentIcon,
    unconfirmed: SentIcon,
};

// An outcome glyph owns its colour in CSS, where the theme can answer for it;
// only the identity tone is painted per item (the rail colour, inline).
const TONE_CLASS: Record<LeadTone, string | undefined> = {
    identity: undefined,
    bad: styles.chipFailIcon,
    ok: styles.chipSentIcon,
    quiet: styles.chipUnconfirmedIcon,
};

// Why a chip refuses to be dragged, said in the user's language. Exported so the
// hover card can say the same thing: the chip itself no longer carries a native
// tooltip, so the hover card is the only place left to read the reason.
export const REFUSAL_KEY: Record<string, string> = {
    'sequence-step': 'calendar.grid.refusalSequence',
    'campaign-too-close': 'calendar.grid.refusalTooClose',
    'campaign-started': 'calendar.grid.refusalStarted',
    'already-sent': 'calendar.grid.refusalSent',
};

/**
 * The one chip used by every view. Sizes differ; the model behind it does not.
 * Click opens the row menu (positioned at the pointer, matching the rest of the
 * app); pointer-down begins a drag when the event is eligible.
 */
const EventChipComponent: React.FC<Props> = ({
    event, now, size = 'month', isDragging, content, onOpen, onDragStart, onHoverStart, onHoverEnd, onDelete,
}) => {
    const { t } = useTranslation(namespace);
    const vm = useMemo(() => buildChipViewModel(event, now), [event, now]);
    const eligibility = useMemo(() => canDragEvent(event, now), [event, now]);

    // A chip IS one occurrence — the only thing in the product that knows WHICH
    // one. Every other delete surface knows a document and can therefore only
    // ever ask about its next occurrence, which is why deleting the third of
    // five was unreachable. A campaign or sequence step answers 'none' and gets
    // nothing here: it is not a message document and deleting one means
    // something else.
    const deletable = useMemo(() => planOccurrenceDelete(event).kind !== 'none', [event]);
    const canDelete = deletable && !!onDelete;
    const requestDelete = (mouse?: { x: number; y: number }) => onDelete?.(event, mouse);
    // A focused chip answers Delete in onKeyDown below; this is the same gesture for a
    // mouse user, whose click opens the editor and so can never leave the chip focused:
    // the chip under the pointer answers Delete (see use-pointed-delete for the guards).
    const deleteAtCentre = (el: HTMLElement) => {
        onHoverEnd?.();
        // No pointer position is part of a key press — anchor any menu at the chip's centre.
        const r = el.getBoundingClientRect();
        requestDelete({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    };
    const pointed = usePointedDelete(canDelete ? deleteAtCentre : null);

    // An asset URL that 404s (expired, or the asset was deleted) used to paint
    // the browser's broken-image frame — and on a chip whose plan is image-only
    // it painted an EMPTY chip, which on a row of pictures reads as "this one
    // failed to load" about a send that is perfectly healthy. A chip that cannot
    // show its picture is just a chip without one, so it falls back to the text
    // treatment. Local state on purpose: no cache, and a chip that is re-pointed
    // at a different asset gets a fresh chance at it.
    const [imageFailed, setImageFailed] = useState(false);
    useEffect(() => setImageFailed(false), [vm.mediaUrl]);

    // Inside the extension the chip is painted into web.whatsapp.com, whose CSP
    // does not list our asset bucket — an <img> pointed at it is refused before
    // it reaches the network, which is why the thumbnail work the product owner
    // asked for was invisible to every extension user while looking perfectly
    // fine on the web app. This resolves the asset through the background
    // service worker (not bound by a page's CSP) into a `data:` URL, which that
    // same CSP does allow, and hands back the untouched URL on the website. One
    // resolution per asset, shared across every chip pointing at it.
    const { resolvedUrl, isLoading } = useAssetUrl(vm.mediaUrl ?? '');
    const thumbSrc = chipThumbnailSrc({ mediaUrl: vm.mediaUrl, resolvedUrl, isLoading, imageFailed });

    const planned = content ?? FULL_CHIP;
    const canShowImage = !!thumbSrc;
    const show = canShowImage ? planned : planWithoutImage(planned);

    // The picture is all this chip can afford, so it stops being a thumbnail
    // beside other things and becomes the chip itself — see .chipImageOnly.
    const imageOnly = show.image && !show.text && !show.time;

    // ONE rule for the leading glyph: once a send has RESOLVED, that slot says
    // how it went; until then it says what the thing is.
    //
    // The failure half of this was already true. The sent half was not: a sent
    // chip carried a tiny 11px tick BESIDE its kind icon, so the state most
    // people are scanning for was the smallest mark on the chip and shared its
    // corner with a glyph repeating what the title already said. On a month
    // that is mostly failures the inverse scan — "so which one DID go out?" —
    // had nothing to land on. It gets the same full-size outlined glyph the
    // failure gets now, in green rather than red: presence without bringing
    // back the red fill, which is what made a bad month unreadable in the first
    // place. Identity still lives on the rail, so the slot is spent on the state
    // rather than doubled up, and the chip is no busier than before — it trades
    // two marks for one.
    //
    // 'unconfirmed' is deliberately the SAME glyph in the tertiary ink: it went
    // out, we are only missing the receipt, and giving it a third shape would
    // invent a distinction the user cannot act on.
    //
    // A WhatsApp Status is the one kind this rule cannot apply to as written:
    // its title is its own text, so the kind glyph is the only thing saying it
    // is a Status. It keeps its ring as a SECOND mark beside the outcome, never
    // tinted by it — see layout/lead-glyph.ts for the order and what a narrow
    // lane drops first.
    const leadMarks = planLeadMarks(vm.kind, vm.status, show.text);

    const className = [
        styles.chip,
        size === 'time' ? styles.chipTimeSize : size === 'panel' ? styles.chipPanelSize : '',
        vm.isPaused ? styles.chipPaused : '',
        vm.status === 'sent' || vm.status === 'unconfirmed' ? styles.chipSent : '',
        eligibility.draggable ? '' : styles.chipNotDraggable,
        isDragging ? styles.chipDragging : '',
        show.text ? '' : styles.chipCompact,
        imageOnly ? styles.chipImageOnly : '',
    ].filter(Boolean).join(' ');

    // aria-label, NOT title: a `title` paints the browser's own black tooltip on
    // top of our hover card, so the user got two descriptions of the same chip.
    // aria-label names the chip for screen readers and renders nothing.
    //
    // The chip's name overrides its contents, so the Status ring's own label
    // never reaches a screen reader through it — the name has to say "Status"
    // itself, or a Status is announced exactly like a message.
    const name = vm.kind !== 'status'
        ? vm.title
        : vm.title
            ? t('calendar.grid.statusChipLabel', { title: vm.title })
            : t('calendar.grid.statusKind');
    // A campaign that did not reach everyone says how many it did. The failure mark leads
    // the chip (red = something did not go out); this says how much.
    const splitText = vm.split?.kind === 'of'
        ? t('calendar.grid.splitSent', { sent: vm.split.sent, total: vm.split.total })
        : vm.split?.kind === 'failed'
            ? t('calendar.grid.countFailed', { count: vm.split.count })
            : undefined;
    const named = splitText ? `${name} — ${splitText}` : name;
    const label = eligibility.refusal
        ? `${named} — ${t(REFUSAL_KEY[eligibility.refusal])}`
        : named;

    return (
        <div
            className={className}
            style={{ ['--chip-rail' as any]: vm.railColor }}
            role="button"
            tabIndex={0}
            aria-label={label}
            data-occurrence-key={vm.key}
            onPointerDown={e => {
                onHoverEnd?.();
                if (eligibility.draggable) onDragStart(event, e);
            }}
            onPointerEnter={e => {
                if (e.pointerType !== 'mouse') return;
                if (canDelete) pointed.start(e.currentTarget as HTMLElement);
                onHoverStart?.(event, (e.currentTarget as HTMLElement).getBoundingClientRect());
            }}
            onPointerLeave={() => { pointed.stop(); onHoverEnd?.(); }}
            onClick={e => onOpen(event, { x: e.clientX, y: e.clientY })}
            // Right-click is the gesture; the confirmation is the safety. Nothing
            // is written here — it opens the same scope dialog the sidebar uses,
            // carrying THIS occurrence's instant, and a cancel writes nothing.
            // The browser's own menu is suppressed only when we have something to
            // put in its place.
            onContextMenu={canDelete ? (e => { e.preventDefault(); e.stopPropagation(); onHoverEnd?.(); requestDelete({ x: e.clientX, y: e.clientY }); }) : undefined}
            onKeyDown={e => {
                if (canDelete && (e.key === 'Delete' || e.key === 'Backspace')) {
                    e.preventDefault();
                    deleteAtCentre(e.currentTarget as HTMLElement);
                    return;
                }
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                onOpen(event, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
            }}
        >
            {/* Never dropped when it is an OUTCOME: a narrow lane sheds the kind
                icon, because the title already says what the thing is — but
                shedding the mark that says whether it went out is how the grid
                stops answering its own question. Sent and failed both survive;
                a resolved Status's ring goes before them. */}
            {leadMarks.map(mark => {
                const Icon = LEAD_ICON[mark.glyph];
                return (
                    <Icon
                        key={mark.glyph}
                        className={[styles.chipIcon, TONE_CLASS[mark.tone]].filter(Boolean).join(' ')}
                        // Only an identity mark is tinted per item; an outcome
                        // owns its colour in CSS, where the theme can answer for it.
                        style={mark.tone === 'identity' ? { color: vm.railColor } : undefined}
                        // An outcome and a Status are announced. A plain kind icon
                        // repeats what the title already says, so it stays hidden
                        // rather than becoming an unlabelled img role. (MUI hides its
                        // own icons by default; the lucide-drawn Status ring does not,
                        // hence the explicit attribute.)
                        {...(mark.labelKey ? { role: 'img', 'aria-label': t(mark.labelKey) } : { 'aria-hidden': true })}
                    />
                );
            })}
            {show.time && <span className={styles.chipTime}>{vm.time}</span>}
            {show.text && <span className={styles.chipTitle}>{vm.title}</span>}
            {show.text && vm.stepLabel && (
                <span className={styles.chipStep} aria-label={t('calendar.grid.partOfSequence')}>{vm.stepLabel}</span>
            )}
            {/* Kept on a narrow lane: like the outcome mark, "how many went out" is the
                answer the chip exists to give, so it outlasts the title. */}
            {splitText && <span className={styles.chipSplit} aria-hidden="true">{splitText}</span>}
            {show.text && vm.isRecurring && <span className={styles.chipRecurring} aria-label={t('calendar.grid.repeats')}>↻</span>}
            {show.image && (
                <img
                    className={styles.chipThumb}
                    src={thumbSrc}
                    alt=""
                    onError={() => setImageFailed(true)}
                />
            )}
        </div>
    );
};

/**
 * Memoised because a drag updates the container's state on EVERY pointer move,
 * which re-renders the grid: without this, dragging one chip across a dense
 * month re-rendered all ~200 of them per mouse move. The event objects come
 * from a memoised selector and the callbacks are stable, so the only chip that
 * actually re-renders during a drag is the one being dragged (its `isDragging`
 * flips) — and the one under the pointer in the time grid.
 */
export const EventChip = React.memo(EventChipComponent);
